import { useCallback, useEffect, useMemo, useState } from "react";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import { Collapse, Divider, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router";
import { billtrustBaseURL, cabinetworksGroupBaseURL, salesforceBaseURL } from "constants/endpoints";
import useB2CAccount from "auth/useB2CAccount";
import { models } from "types/api/viewModels.ts";
import { CabinetworksGroupApplication, NavigationActionType } from "types/declarations/enums.ts";
import * as Styles from "./topNavigationBar.styles";
import DesktopNavigationMenus from "./DesktopNavigationMenus";
import { useDispatch, useSelector } from "react-redux";
import { newOrderActions } from "features/reducers/newOrder/newOrder.ts";
import { RootState } from "stores/application.store";
import { hasOrderLineItems } from "utils/order";
import { useUserLocation } from "hooks/useUserLocation.ts";

interface TopNavigationBarProps {
	navigation?: models["NavigationViewModel"];
	userDetails?: models["UserViewModel"];
}

type TopNavigationMenu = models["NavigationMenuViewModel"] & {
	items: models["NavigationItemViewModel"][];
};

const sortByOrder = <
	T extends {
		sortOrder?: number;
	}
>(
	first: T,
	second: T
) => (first.sortOrder ?? Number.MAX_SAFE_INTEGER) - (second.sortOrder ?? Number.MAX_SAFE_INTEGER);

const normalizePath = (path?: string | null) => {
	if (!path) return undefined;

	let normalizedPath = path;
	while (normalizedPath.endsWith("/") && normalizedPath.length > 1) {
		normalizedPath = normalizedPath.slice(0, -1);
	}
	return normalizedPath.length === 0 ? "/" : normalizedPath;
};

const isPathActive = (pathname: string, path?: string | null) => {
	const normalizedCurrentPath = normalizePath(pathname);
	const normalizedTargetPath = normalizePath(path);

	if (!normalizedCurrentPath || !normalizedTargetPath) return false;
	if (normalizedTargetPath === "/") return normalizedCurrentPath === normalizedTargetPath;

	return (
		normalizedCurrentPath === normalizedTargetPath || normalizedCurrentPath.startsWith(`${normalizedTargetPath}/`)
	);
};

const resolveExternalApplicationUrl = (application?: string | null, path?: string | null) => {
	if (!path) return undefined;

	// If the path is already a full URL, return it as is
	if (/^https?:\/\//i.test(path)) return path;

	const normalizedPath = path.startsWith("/") ? path : `/${path}`;

	switch (application) {
		case CabinetworksGroupApplication.Salesforce:
			return salesforceBaseURL ? `${salesforceBaseURL.replace(/\/$/, "")}${normalizedPath}` : path;
		case CabinetworksGroupApplication.BillTrust:
			return billtrustBaseURL ? `${billtrustBaseURL.replace(/\/$/, "")}${normalizedPath}` : path;
		case CabinetworksGroupApplication.CabinetworksGroup:
			return `${cabinetworksGroupBaseURL}${normalizedPath}`;
		default:
			return path;
	}
};

const hasNavigationItems = (menu: models["NavigationMenuViewModel"]): menu is TopNavigationMenu =>
	Array.isArray(menu.items) && menu.items.length > 0;

const TopNavigationBar = ({ navigation, userDetails }: TopNavigationBarProps) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const { isProtectedNewOrderFlowPage } = useUserLocation();
	const dispatch = useDispatch();
	const { logout } = useB2CAccount();
	const parsedCSV = useSelector((state: RootState) => state.newOrder.parsedCSV);
	const newOrderDetails = useSelector((state: RootState) => state.newOrder.newOrderDetails);
	const draftOrder = useSelector((state: RootState) => state.newOrder.draftDetails?.draftOrder);
	const validatedOrderResponse = useSelector((state: RootState) => state.newOrder.validatedOrderResponse);
	const leaveNewOrderFlow = useSelector((state: RootState) => state.newOrder.leaveNewOrderFlow);
	const isCondensedNavigation = useMediaQuery(theme.breakpoints.down("lg"));
	const resolvedNavigation = navigation ?? userDetails?.navigation;
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const [openMobileSectionId, setOpenMobileSectionId] = useState<string | null>(null);

	const navigationMenus = useMemo(() => {
		return [...(resolvedNavigation?.menus ?? [])]
			.filter(hasNavigationItems)
			.map((menu) => ({
				...menu,
				items: [...menu.items].sort(sortByOrder)
			}))
			.sort(sortByOrder)
			.filter(
				(menu) =>
					Boolean(menu.title) &&
					menu.items.some(
						(item) =>
							Boolean(item.title) &&
							(Boolean(item.path) || item.actionType === NavigationActionType.LOGOUT)
					)
			);
	}, [resolvedNavigation]);

	const primaryActions = useMemo(
		() =>
			[...(resolvedNavigation?.primaryActions ?? [])]
				.filter(
					(item) =>
						Boolean(item?.title) &&
						(Boolean(item?.path) || item?.actionType === NavigationActionType.LOGOUT)
				)
				.sort(sortByOrder),
		[resolvedNavigation]
	);

	const mobilePrimaryAction = primaryActions[0];
	const overflowPrimaryActions = primaryActions.slice(1);
	const homePath = normalizePath(resolvedNavigation?.home?.path);
	const currentPath = normalizePath(pathname);
	const isHomePage = Boolean(homePath) && currentPath === homePath;
	const shouldConfirmLeaveNewOrderFlow =
		hasOrderLineItems(parsedCSV, newOrderDetails, draftOrder, validatedOrderResponse) &&
		isProtectedNewOrderFlowPage;
	const homeNavigationItem = resolvedNavigation?.home?.path
		? ({
				title: resolvedNavigation.home.title,
				path: resolvedNavigation.home.path,
				application: resolvedNavigation.home.application
			} as models["NavigationItemViewModel"])
		: undefined;

	const handleCloseMobileMenu = () => {
		setIsMobileMenuOpen(false);
		setOpenMobileSectionId(null);
	};

	const handleToggleMobileMenu = () => {
		setIsMobileMenuOpen((current) => !current);
	};

	const handleToggleMobileSection = (sectionId: string) => {
		setOpenMobileSectionId((current) => (current === sectionId ? null : sectionId));
	};

	const executeNavigation = useCallback(
		(item: models["NavigationItemViewModel"]) => {
			if (item.actionType === NavigationActionType.LOGOUT) {
				void logout();
				return;
			}

			if (!item.path) return;
			if (item.application === CabinetworksGroupApplication.Tandem) {
				navigate(item.path);
				return;
			}

			const externalUrl = resolveExternalApplicationUrl(item.application, item.path);
			if (externalUrl) {
				window.location.href = externalUrl;
			}
		},
		[logout, navigate]
	);

	const navigateToItem = useCallback(
		(item: models["NavigationItemViewModel"]) => {
			handleCloseMobileMenu();

			if (
				item.application === CabinetworksGroupApplication.CabinetworksGroup &&
				item.actionType !== NavigationActionType.LOGOUT
			) {
				const externalUrl = resolveExternalApplicationUrl(item.application, item.path);
				if (externalUrl) window.open(externalUrl, "_blank", "noopener,noreferrer");
				return;
			}

			if (shouldConfirmLeaveNewOrderFlow) {
				dispatch(newOrderActions.leaveNewOrderFlow({ state: "pause", navItem: item }));
				return;
			}

			executeNavigation(item);
		},
		[dispatch, executeNavigation, shouldConfirmLeaveNewOrderFlow]
	);

	useEffect(() => {
		if (leaveNewOrderFlow?.state !== "proceed" || !leaveNewOrderFlow.navItem) return;

		executeNavigation(leaveNewOrderFlow.navItem);
		dispatch(newOrderActions.resetNewOrderFlow());
	}, [dispatch, executeNavigation, leaveNewOrderFlow]);

	const handleNavigateHome = () => {
		if (!homeNavigationItem) return;

		navigateToItem(homeNavigationItem);
	};

	const isCurrentSection = (menu: TopNavigationMenu) =>
		!isHomePage &&
		menu.id !== "nav-dashboard" &&
		menu.items.some((item) => {
			const itemPath = normalizePath(item.path);
			if (!itemPath || itemPath === homePath) return false;

			return isPathActive(pathname, item.path);
		});

	if (!resolvedNavigation?.home?.path && navigationMenus.length === 0 && primaryActions.length === 0) return null;

	if (isCondensedNavigation) {
		return (
			<Styles.NavigationAppBar
				position="sticky"
				data-testid="top-navigation-bar"
			>
				<Styles.NavigationToolbar disableGutters>
					<Styles.MobileNavigationBar>
						<Styles.MobileNavigationLeading>
							<Styles.HamburgerButton
								aria-expanded={isMobileMenuOpen}
								aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
								onClick={handleToggleMobileMenu}
							>
								<MenuRoundedIcon />
							</Styles.HamburgerButton>
							<Styles.BrandButton
								type="button"
								onClick={handleNavigateHome}
								aria-label="Go to home"
							>
								<Styles.BrandImage
									src="/assets/tandem_logos/tandem.png"
									alt="Tandem"
								/>
							</Styles.BrandButton>
						</Styles.MobileNavigationLeading>
						{mobilePrimaryAction && (
							<Styles.PrimaryActionButton
								variant="contained"
								color="primary"
								disableElevation
								onClick={() => navigateToItem(mobilePrimaryAction)}
							>
								{mobilePrimaryAction.title}
							</Styles.PrimaryActionButton>
						)}
					</Styles.MobileNavigationBar>
				</Styles.NavigationToolbar>
				<Collapse
					in={isMobileMenuOpen}
					timeout="auto"
					unmountOnExit
				>
					<Styles.MobileNavigationPanel>
						{navigationMenus.map((menu) => {
							const menuItems = menu.items.filter(
								(item) => item.title && (item.path || item.actionType === NavigationActionType.LOGOUT)
							);
							const sectionId = menu.id ?? menu.title ?? "mobile-navigation-section";
							const isSectionOpen = openMobileSectionId === sectionId;
							const dashboardItem = menuItems.find(
								(item) => item.id === "nav-dashboard-home" || item.id === "nav-dashboard-item"
							);
							const isDashboardMenu = menu.id === "nav-dashboard" && Boolean(dashboardItem);

							if (menuItems.length === 0) return null;

							return (
								<Styles.MobileNavigationSection key={sectionId}>
									<Styles.MobileNavigationSectionButton
										isSelected={isCurrentSection(menu)}
										onClick={() =>
											isDashboardMenu && dashboardItem
												? navigateToItem(dashboardItem)
												: handleToggleMobileSection(sectionId)
										}
										aria-expanded={isDashboardMenu ? undefined : isSectionOpen}
										aria-controls={isDashboardMenu ? undefined : `${sectionId}-children`}
										aria-current={isCurrentSection(menu) ? "page" : undefined}
									>
										<Styles.MobileNavigationSectionLabel>
											{menu.title}
										</Styles.MobileNavigationSectionLabel>
										{!isDashboardMenu && <Styles.MobileNavigationChevron isOpen={isSectionOpen} />}
									</Styles.MobileNavigationSectionButton>
									{!isDashboardMenu && (
										<Collapse
											in={isSectionOpen}
											timeout="auto"
											id={`${sectionId}-children`}
										>
											<Styles.MobileNavigationChildren>
												{menuItems.map((item) => (
													<Styles.MobileNavigationItemButton
														key={item.id ?? item.title}
														onClick={() => navigateToItem(item)}
													>
														{item.title}
													</Styles.MobileNavigationItemButton>
												))}
											</Styles.MobileNavigationChildren>
										</Collapse>
									)}
								</Styles.MobileNavigationSection>
							);
						})}
						{overflowPrimaryActions.length > 0 && (
							<>
								<Divider />
								<Styles.MobileNavigationSection>
									<Styles.MobileNavigationSectionLabel>Actions</Styles.MobileNavigationSectionLabel>
									{overflowPrimaryActions.map((action) => (
										<Styles.MobileNavigationItemButton
											key={action.id ?? action.title}
											onClick={() => navigateToItem(action)}
										>
											{action.title}
										</Styles.MobileNavigationItemButton>
									))}
								</Styles.MobileNavigationSection>
							</>
						)}
					</Styles.MobileNavigationPanel>
				</Collapse>
			</Styles.NavigationAppBar>
		);
	}

	return (
		<Styles.NavigationAppBar
			position="sticky"
			data-testid="top-navigation-bar"
		>
			<Styles.NavigationToolbar disableGutters>
				<Styles.BrandButton
					type="button"
					onClick={handleNavigateHome}
					aria-label="Go to home"
				>
					<Styles.BrandImage
						src="/assets/tandem_logos/tandem.png"
						alt="Tandem"
					/>
				</Styles.BrandButton>
				<Styles.NavigationControls>
					<DesktopNavigationMenus
						key={pathname}
						menus={navigationMenus}
						isCurrentSection={isCurrentSection}
						onNavigate={navigateToItem}
					/>
					<Styles.PrimaryActions>
						{primaryActions.map((action) => (
							<Styles.PrimaryActionButton
								key={action.id ?? action.title}
								variant="contained"
								color="primary"
								disableElevation
								onClick={() => navigateToItem(action)}
							>
								{action.title}
							</Styles.PrimaryActionButton>
						))}
					</Styles.PrimaryActions>
				</Styles.NavigationControls>
			</Styles.NavigationToolbar>
		</Styles.NavigationAppBar>
	);
};

export default TopNavigationBar;
